import { useState } from "react";
import downlaodBillIcon from "../../assets/images/svg/billing-download.svg";
import billingShowMore from "../../assets/images/svg/billig-showmore.svg";
import { useCreatePortalSessionMutation } from "../../components/apis/metaBuilderApi";

export type MetaAdsInvoice = {
  id: string;
  date: string;
  status: string;
  amount: string;
  pdfUrl?: string;
  invoiceId?: string;
};

type MetaAdsBillingDetailsProps = {
  billingName: string;
  billingAddress: string;
  email: string;
  invoices: MetaAdsInvoice[];
  onUpdateBilling?: () => void;
  userId?: string;
  className?: string;
};

export default function MetaAdsBillingDetails({
  billingName,
  billingAddress,
  email,
  invoices,
  onUpdateBilling,
  userId,
  className = "mt-8",
}: MetaAdsBillingDetailsProps) {
  const [visibleInvoiceCount, setVisibleInvoiceCount] = useState(4);
  const [createPortalSession, { isLoading: isCreatingPortalSession }] =
    useCreatePortalSessionMutation();

  const handleUpdateBilling = async () => {
    if (!userId) {
      console.error("userId is required to update billing");
      return;
    }

    try {
      // Call the backend to create a Stripe Customer Portal session
      const result = await createPortalSession({
        userId,
        returnUrl: window.location.href, // Return to the same page after updating
      }).unwrap();

      // Redirect to Stripe Customer Portal
      window.location.href = result.url;
    } catch (error) {
      console.error("Error redirecting to billing portal:", error);
      // Fallback to original callback if provided
      if (onUpdateBilling) {
        onUpdateBilling();
      }
    }
  };

  return (
    <section className={`${className} border-[#1A1D26] pt-8 mt-[-30px]`}>
      <h2 className="text-white text-[24px] lg:text-[36px] lg:font-bold font-semibold">
        Meta ads tool payments and billing
      </h2>

      <div className="lg:mt-8 mt-4">
        <p className="text-white text-[15px] lg:text-[20px] font-medium leading-[1.4]">
          Billing Name: <span className="font-semibold">{billingName}</span>
        </p>
        <p className="mt-2 text-white text-[15px] lg:text-[20px] font-medium leading-[1.4]">
          Billing Address: <span className="font-semibold">{billingAddress || "N/A"}</span>
        </p>
        <p className="mt-2 text-white text-[15px] lg:text-[20px] font-medium leading-[1.4]">
          Billing Email: <span className="font-semibold">{email}</span>
        </p>
        <button
          type="button"
          onClick={handleUpdateBilling}
          disabled={isCreatingPortalSession}
          className="mt-2 lg:mt-5 w-full lg:w-auto px-[24px] py-[12px] rounded-full bg-white text-black text-[16px] font-semibold mx-auto lg:mx-0 block disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCreatingPortalSession ? "Loading..." : "Update billing and payment info"}
        </button>
      </div>

      <div className="mt-8 lg:mt-10">
        <h3 className="text-white text-[20px] lg:text-[24px] font-bold">
          Invoices:
        </h3>

        {/* ── MOBILE: stacked cards ── */}
        <div className="mt-4 flex flex-col gap-3 lg:hidden">
          {invoices.slice(0, visibleInvoiceCount).map((invoice, index) => (
            <div
              key={`mob-${invoice.id}-${index}`}
              className="rounded-[14px] bg-[#1A1B1F] border border-[#1A1D26] overflow-hidden"
            >
              {/* Invoice ID */}
              <div className="border-b border-[#82879833] mx-4">
                <div className="px-5 py-5 pt-[30px]">
                  <p className="text-white text-[16px] font-medium">
                    {invoice.id}
                  </p>
                </div>
              </div>

              {/* Date */}
              <div className="border-b border-[#82879833] mx-4">
                <div className="px-5 py-5">
                  <p className="text-white text-[16px] font-medium">
                    {invoice.date}
                  </p>
                </div>
              </div>

              {/* Status */}
              <div className="border-b border-[#82879833] mx-4">
                <div className="px-5 py-5">
                  <p className="text-white text-[16px] font-medium lowercase">
                    {invoice.status}
                  </p>
                </div>
              </div>

              {/* Amount */}
              <div className="border-b border-[#82879833] mx-4">
                <div className="px-5 py-5">
                  <p className="text-white text-[16px] font-medium">
                    {invoice.amount}
                  </p>
                </div>
              </div>

              {/* Download */}
              <div>
                <div className="px-5 py-5 pb-[30px] flex justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      invoice.pdfUrl &&
                      window.open(
                        invoice.pdfUrl,
                        "_blank",
                        "noopener,noreferrer",
                      )
                    }
                    disabled={!invoice.pdfUrl}
                    className="inline-flex items-center gap-2 text-white text-[16px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Download PDF
                    <img
                      src={downlaodBillIcon}
                      alt="Download"
                      className="w-[22px] h-[22px]"
                    />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* ── DESKTOP: original table rows (unchanged) ── */}
        <div className="hidden lg:block mt-5 border-y border-[#1A1D26]">
          {invoices.slice(0, visibleInvoiceCount).map((invoice, index) => (
            <div
              key={`desk-${invoice.id}-${index}`}
              className="grid grid-cols-[1.2fr_1fr_0.8fr_1fr_1fr] items-center gap-6 py-4 border-b border-[#1A1D26] last:border-b-0"
            >
              <p className="text-white text-[16px] font-medium">{invoice.id}</p>
              <p className="text-white text-[16px] font-medium">
                {invoice.date}
              </p>
              <p className="text-white text-[16px] font-medium lowercase">
                {invoice.status}
              </p>
              <p className="text-white text-[16px] font-medium">
                {invoice.amount}
              </p>
              <button
                type="button"
                onClick={() =>
                  invoice.pdfUrl &&
                  window.open(invoice.pdfUrl, "_blank", "noopener,noreferrer")
                }
                disabled={!invoice.pdfUrl}
                className="justify-self-end inline-flex items-center gap-3 text-white text-[16px] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Download PDF
                <img
                  src={downlaodBillIcon}
                  alt="Download"
                  className="w-[24px] h-[24px]"
                />
              </button>
            </div>
          ))}
        </div>

        {visibleInvoiceCount < invoices.length && (
          <button
            type="button"
            onClick={() => setVisibleInvoiceCount((prev) => prev + 4)}
            className="mt-6 inline-flex items-center gap-2 text-[#58F2A9] text-[16px] lg:text-[18px] font-medium"
          >
            Show more
            <img
              src={billingShowMore}
              alt="Show more"
              className="w-[24px] h-[24px]"
            />
          </button>
        )}
      </div>
    </section>
  );
}
